import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { SAMPLE_PROJECT_ID } from "~/constants/sampleProject";
import { ProjectApi } from "~/functions/api/project";
import { useAuthGuard } from "~/hooks/useAuthGuard";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import ContextMenu from "~/images/context_menu.svg?react";
import EmptyProjectIcon from "~/images/empty_project.svg?react";
import MembersIcon from "~/images/more_than_three_members.svg?react";
import SingleMemberIcon from "~/images/single_member.svg?react";
import type { ProjectMemberInfo, Projects } from "~/interface/project";
import { setCurrentProject, setIntendedUrl, setProjectMembers } from "~/slices/auth";
import type { RootState } from "~/store"; // Reduxの型
import { dateToString } from "~/utils/customDate";
import DeleteProjectModal from "../deleteProjectModal";
import styles from "./index.module.scss";

interface MemberProps {
  members: ProjectMemberInfo[];
  SingleMemberIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  MembersIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

interface ProjectListProps {
  projects: Projects[];
  isLoading: boolean;
  onRefresh: () => void;
  onHideSampleProject?: () => void;
  onSelectSampleProject?: () => void;
}

export const Member: React.FC<MemberProps> = ({ members, SingleMemberIcon, MembersIcon }) => {
  const displayMembers = members.slice(0, 3); // 最大3人分のアイコンを表示

  return (
    <div className={styles["memberIconWrapper"]}>
      {members.length > 2 ? (
        <MembersIcon />
      ) : (
        displayMembers.map((_, index) => <SingleMemberIcon key={`member-icon-${index}`} />)
      )}
    </div>
  );
};

const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  isLoading,
  onRefresh,
  onHideSampleProject,
  onSelectSampleProject,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<{
    id: string;
    name: string;
    isSample?: boolean;
  } | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // 認証チェック用のフックを使用
  const { ensure } = useAuthGuard();

  // 権限チェック用のフックを使用（現在は未使用だがフック初期化は必要）
  useProjectPermission();

  // Function to check if current user can delete the project
  // Only users with "owner" role can delete projects according to backend business rules
  const canDeleteProject = useCallback(
    (projectId: string, projectUsers: ProjectMemberInfo[]): boolean => {
      if (projectId === SAMPLE_PROJECT_ID) {
        return true;
      }
      const currentUserInProject = projectUsers.find(
        (member) => member.user_id === currentUser?.id,
      );
      return currentUserInProject?.role === "owner";
    },
    [currentUser?.id],
  );

  const handleProjectSelect = async (projectId: string) => {
    if (projectId === SAMPLE_PROJECT_ID) {
      onSelectSampleProject?.();
      return;
    }

    // 認証チェック: currentUserが無効な場合はリダイレクト
    const authIds = ensure();
    if (!authIds) {
      return;
    }

    const response = await handleGetProject(projectId);
    if (response !== null) {
      dispatch(setCurrentProject(response));

      // Fetch and save project members to Redux
      const projectApi = new ProjectApi();
      const members = await projectApi.getMembers(projectId, authIds.organizationId);
      if (members) {
        dispatch(setProjectMembers(members));
      }

      navigate(`/${authIds.organizationId}/project/${projectId}/dashboard`);
    }
  };

  const handleGetProject = async (projectId: string) => {
    // 認証チェック: currentUserが無効な場合はリダイレクト
    const authIds = ensure();
    if (!authIds) {
      return null;
    }

    const projectApi = new ProjectApi();

    const response = await projectApi.get(authIds.organizationId, projectId);
    // プロジェクト取得失敗時は/signinにリダイレクト
    if (response === null) {
      const currentPath = location.pathname + location.search + location.hash;
      if (currentPath !== "/signin" && currentPath !== "/signup") {
        dispatch(setIntendedUrl(currentPath));
      }
      navigate("/signin");
      return null;
    }
    return response;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  const handleOpenModal = (projectId: string, projectName: string, isSample: boolean) => {
    setSelectedProject({ id: projectId, name: projectName, isSample });
    setIsDeleteProjectModalOpen(true);
  };

  const handleCloseModal = (shouldRefresh = true) => {
    setIsDeleteProjectModalOpen(false);
    setSelectedProject(null);
    setDropdownOpen(null);
    if (shouldRefresh) {
      onRefresh();
    }
  };

  const handleSampleHide = async () => {
    onHideSampleProject?.();
    handleCloseModal(false);
  };

  return (
    <div data-testid="projects-list">
      {projects && projects.length > 0 ? (
        <table className={styles["table"]}>
          <thead>
            <tr>
              <th>プロジェクト名</th>
              <th>契約期間</th>
              <th>更新日時</th>
              <th>メンバー</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project, index) => {
              const isSampleProjectRow = project.project.id === SAMPLE_PROJECT_ID;
              const canRemove = canDeleteProject(project.project.id, project.users);
              return (
                <tr
                  style={{ cursor: "pointer" }}
                  key={`${project.project.id}-${index}`}
                  onClick={() => handleProjectSelect(project.project.id)}
                  data-testid="project-item"
                >
                  <td className={styles["projectName"]}>{project.project.name}</td>
                  {!project.project.start_date && !project.project.end_date ? (
                    <td>-</td>
                  ) : (
                    <td>
                      {project.project.start_date
                        ? dateToString(new Date(project.project.start_date))
                            .split("T")[0]!
                            .replace(/-/g, "/")
                        : ""}{" "}
                      {project.project.end_date
                        ? "~" +
                          dateToString(new Date(project.project.end_date))
                            .split("T")[0]!
                            .replace(/-/g, "/")
                        : ""}
                    </td>
                  )}
                  <td>
                    {project.project.modified_at
                      ? dateToString(new Date(project.project.modified_at))
                          .split("T")[0]!
                          .replace(/-/g, "/")
                      : ""}
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Member
                        members={project.users}
                        SingleMemberIcon={SingleMemberIcon}
                        MembersIcon={MembersIcon}
                      />
                      <span className={styles["members"]}>{project.users.length}人</span>
                    </div>
                  </td>
                  <td>
                    <div
                      className={styles["dropdownWrapper"]}
                      onMouseLeave={() => setDropdownOpen(null)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(index);
                      }}
                    >
                      <ContextMenu />
                      {dropdownOpen === index && ( // ホバー中に表示
                        <div className={styles["dropdownMenu"]}>
                          <ul>
                            <li
                              className={canRemove ? styles["danger"] : styles["disabled"]}
                              onClick={(e) => {
                                if (!canRemove) return;
                                e.stopPropagation();
                                handleOpenModal(
                                  project.project.id,
                                  project.project.name,
                                  isSampleProjectRow,
                                );
                              }}
                            >
                              削除{" "}
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className={styles["emptyState"]}>
          <EmptyProjectIcon />
          <p>プロジェクトがありません。新しいプロジェクトを作成してみましょう。</p>
        </div>
      )}
      {isDeleteProjectModalOpen && selectedProject && (
        <DeleteProjectModal
          onClose={handleCloseModal}
          projectName={selectedProject.name}
          projectId={selectedProject.id}
          onConfirm={selectedProject.isSample ? handleSampleHide : undefined}
          confirmLabel={selectedProject.isSample ? "非表示にする" : undefined}
          description={
            selectedProject.isSample ? (
              <div className={styles["sampleDescription"]}>
                <p>サンプルプロジェクトをホーム画面から非表示にします。</p>
                <p>再度「チュートリアルを見る」を選択すると、いつでも表示し直せます。</p>
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
};

export default ProjectList;
